import React, { useState, useEffect, forwardRef } from "react";
import {
  Box,
  Button,
  Text,
  VStack,
  Grid,
  GridItem,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Select,
  Spinner,
  InputGroup,
  List,
  ListItem,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import * as api from "../../api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAuth } from "../../context/Auth.context";
import { useNavigate } from "react-router-dom";
import axiosRoot from 'axios';

const formatDateISO = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ManageAppointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(getCurrentWeek());
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const { user, isAuthed, ready } = useAuth();
  const navigate = useNavigate();
  const [newDate, setNewDate] = useState("");
  const [timeSlots, setTimeSlots] = useState([]);
  const [closures, setClosures] = useState([]);
  const [fullyBookedDates, setFullyBookedDates] = useState([]);
  const [selectedTime, setSelectedTime] = useState("");
  const [message, setMessage] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isSaving, setIsSaving] = useState(false);

  const {
    isOpen: isBlockingOpen,
    onOpen: onOpenBlocking,
    onClose: onCloseBlocking
  } = useDisclosure();

  const [newBlockDate, setNewBlockDate] = useState("");
  const [blockTimeSlots, setBlockTimeSlots] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [isBlockingSaving, setIsBlockingSaving] = useState(false);
  const [blockMessage, setBlockMessage] = useState("");

  const toggleSlot = (slot) => {
    setSelectedSlots((prev) => {
      if (prev.includes(slot)) {
        return prev.filter((s) => s !== slot);
      }
      return [...prev, slot];
    });
  };

  const CustomDateInput = forwardRef(({ value, onClick }, ref) => (
    <InputGroup mb={4} width="100%">
      <Input
        onClick={onClick}
        ref={ref}
        value={value}
        placeholder="Selecteer een datum"
        readOnly
        cursor="pointer"
        width="100%"
        height="40px"
        fontSize="md"
        marginLeft={'0px'}
      />
    </InputGroup>
  ));

  const { token } = useAuth();

  useEffect(() => {
    console.log(
      "Debug: custom axios Authorization:",
      api.default.defaults.headers["Authorization"]
    );
  }, []);

  useEffect(() => {
    console.log("Debug: token in AuthContext is:", token);
    console.log("Debug: axios defaults:", axiosRoot.defaults.headers['Authorization']);
  }, [token]);

  useEffect(() => {
    if (ready && isAuthed && !user?.roles?.includes('admin')) {
      setMessage("Je moet admin rechten hebben om deze pagina te bekijken.");
      setTimeout(() => navigate("/login"), 2000);
    } else if (ready && !isAuthed) {
      setMessage("Je moet ingelogd zijn om deze pagina te bekijken.");
      setTimeout(() => navigate("/login"), 2000);
    }
  }, [ready, isAuthed, user, navigate]);

  useEffect(() => {
    if (ready && isAuthed && user?.roles?.includes('admin')) {
      fetchAppointments();
    }
  }, [currentWeek, ready, isAuthed, user]);

  useEffect(() => {
    if (ready && isAuthed && user?.roles?.includes('admin')) {
      api.get(`/closures`)
        .then((res) => {
          const dates = res.items.flatMap(({ start_date, end_date }) => {
            const datesInRange = [];
            let current = new Date(start_date);
            const end = new Date(end_date);

            while (current <= end) {
              datesInRange.push(new Date(current));
              current.setDate(current.getDate() + 1);
            }

            return datesInRange;
          });
          setClosures(dates);
        })
        .catch((err) => console.error("Error fetching closures", err));
    }
  }, [ready, isAuthed, user]);

  useEffect(() => {
    if (ready && isAuthed && user?.roles?.includes('admin')) {
      api.get(`/appointments/fully-booked`)
        .then((res) => {
          const dates = res.map(dateStr => new Date(dateStr));
          setFullyBookedDates(dates);
        })
        .catch((err) => console.error("Error fetching fully booked dates", err));
    }
  }, [ready, isAuthed, user]);

  function getCurrentWeek(offset = 0) {
    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate() - start.getDay() + 1 + offset);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }

  const formatAppointmentTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('nl-BE', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Brussels'
    });
  };

  const goToPreviousWeek = () => {
    setCurrentWeek((prev) => {
      const newStart = new Date(prev.start);
      newStart.setDate(newStart.getDate() - 7);
      newStart.setHours(0, 0, 0, 0);
      const newEnd = new Date(newStart);
      newEnd.setDate(newStart.getDate() + 6);
      newEnd.setHours(23, 59, 59, 999);
      return { start: newStart, end: newEnd };
    });
  };

  const goToNextWeek = () => {
    setCurrentWeek((prev) => {
      const newStart = new Date(prev.start);
      newStart.setDate(newStart.getDate() + 7);
      newStart.setHours(0, 0, 0, 0);
      const newEnd = new Date(newStart);
      newEnd.setDate(newStart.getDate() + 6);
      newEnd.setHours(23, 59, 59, 999);
      return { start: newStart, end: newEnd };
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('nl-BE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'Europe/Brussels'
    });
  };

  const handleUnblockAppointment = async (appointmentId) => {
    try {
      await api.post(`/appointments/unblock/${appointmentId}`);
      fetchAppointments();
    } catch (error) {
      console.error("Error unblocking appointment:", error);
    }
  };

  const fetchAppointments = async () => {
    const startISO = formatDateISO(currentWeek.start);
    try {
      const response = await api.get(`/appointments/week?weekStart=${startISO}`);
      setAppointments(response.items);

    } catch (error) {
      console.error("Error fetching appointments:", error);
    }
  };

  const loadAvailableTimeSlots = async (date, setSlots) => {
    setLoadingSlots(true);
    try {
      const response = await api.get(`/appointments/available?date=${date}`);
      const result = response.availableSlots;

      if (Array.isArray(result)) {
        setSlots(result);
      } else {
        setSlots([]);
        alert(result.message || "De winkel is gesloten op deze dag.");
      }
    } catch (error) {
      console.error("Error fetching time slots:", error);
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const moveAppointment = async () => {
    if (!selectedAppointment || !newDate || !selectedTime) {
      setMessage("Gelieve alle velden in te vullen.");
      return;
    }

    const localDateTime = new Date(`${newDate}T${selectedTime}:00`);
    const now = new Date();

    if (localDateTime < now) {
      setMessage("Je kan geen afspraak verplaatsen naar het verleden.");
      return;
    }

    const appointmentDateTime = `${newDate} ${selectedTime}:00`;

    setIsSaving(true);
    setMessage("");

    try {
      await api.put(`/appointments/${selectedAppointment.appointmentId}`, {
        appointment_datetime: appointmentDateTime,
      });

      fetchAppointments();
      onClose();
    } catch (error) {
      console.error("Error updating appointment:", error);
      setMessage("Er is iets misgegaan bij het verplaatsen.");
    } finally {
      setIsSaving(false);
    }
  };

  const blockTimeSlot = async () => {
    if (!newBlockDate || selectedSlots.length === 0) {
      setBlockMessage("Gelieve een datum en minstens één tijdslot te selecteren.");
      return;
    }

    setIsBlockingSaving(true);
    setBlockMessage("");

    try {
      await api.post(`/appointments/block`, {
        date: newBlockDate,
        times: selectedSlots,   
      });

      fetchAppointments();
      onCloseBlocking();
    } catch (error) {
      console.error("Error blocking time slot:", error);
      setBlockMessage("Er is iets misgegaan bij het blokkeren.");
    } finally {
      setIsBlockingSaving(false);
    }
  };

  const daysOfWeek = [...Array(7).keys()].map(i => {
    const date = new Date(currentWeek.start);
    date.setDate(currentWeek.start.getDate() + i);
    return {
      day: date.toLocaleDateString('nl-BE', { weekday: 'long' }),
      date,
    };
  }).filter(({ date }) => date.getDay() !== 5); 

  const isClosureDay = (date) => {
    return closures.some((closureDate) =>
      closureDate.toDateString() === date.toDateString()
    );
  };

  const appointmentsByDay = daysOfWeek.map(({ day, date }) => ({
    day,
    date,
    appointments: appointments.filter(app => {
      const appointmentDate = new Date(app.appointment_datetime);
      return (
        appointmentDate.getFullYear() === date.getFullYear() &&
        appointmentDate.getMonth() === date.getMonth() &&
        appointmentDate.getDate() === date.getDate()
      );
    }),
  }));

  return (
    <Box width="100%" overflowX="auto" mt="20px">
      {!ready ? (
        <Box textAlign="center" mt="120px">
          <Text fontSize="lg" color="gray.500">Aan het laden...</Text>
        </Box>
      ) : !isAuthed ? (
        <Box
          maxW="500px"
          mx="auto"
          mt="120px"
          p={5}
          boxShadow="lg"
          borderRadius="md"
          bg="white"
          textAlign="center"
        >
          <Text color="red.500" fontSize="lg" fontWeight="bold">
            {message}
          </Text>
        </Box>
      ) : (
        <>
          <Text fontSize="2xl" fontWeight="bold" textAlign="center" mb={8}>
            Afspraken van {formatDate(currentWeek.start)} - {formatDate(currentWeek.end)}
          </Text>

          <Box display="flex" justifyContent="center" my={6}>
            <Button mx={2} onClick={goToPreviousWeek}>Vorige week</Button>
            <Button mx={2} onClick={goToNextWeek}>Volgende week</Button>
          </Box>

          <Box textAlign="center" mb={6}>
            <Button colorScheme="red" onClick={onOpenBlocking}>
              Leeg tijdslot blokkeren
            </Button>
          </Box>

          <Grid templateColumns="repeat(6, 1fr)" gap={2} overflowX="auto">
            {appointmentsByDay.map(({ day, date, appointments }) => (
              <GridItem key={day} p={3} bg={isClosureDay(date) ? "red.100" : "gray.200"} borderRadius="md" boxShadow="sm">
                <Text fontSize="md" fontWeight="bold" mb={3} textAlign="center">
                  {day} {formatDate(date)}
                </Text>

                {isClosureDay(date) && (
                  <Text fontSize="sm" color="red.600" textAlign="center" fontWeight="bold" mb={2}>
                    GESLOTEN
                  </Text>
                )}

                {appointments.length > 0 ? (
                  appointments.map(app =>
                    app.userName === "Blokkering" ? (
                      <VStack
                        key={app.appointmentId}
                        p={3}
                        bg="red.100"
                        border="1px solid red"
                        boxShadow="base"
                        borderRadius="md"
                        width="100%"
                        align="center"
                        spacing={2}
                        mb={3}
                      >
                        <CloseIcon w={4} h={4} color="red.600" />
                        <Text fontSize="sm" fontWeight="bold" color="red.600">
                          GEBLOKKEERD
                        </Text>
                        <Text fontSize="sm">
                          <b>Tijdstip:</b> {formatAppointmentTime(app.appointment_datetime)}
                        </Text>
                        <Button size="xs" colorScheme="green" onClick={() => handleUnblockAppointment(app.appointmentId)}>
                          Opheffen
                        </Button>
                      </VStack>
                    ) : (
                      <VStack
                        key={app.appointmentId}
                        p={3}
                        bg="white"
                        boxShadow="base"
                        borderRadius="md"
                        width="100%"
                        align="stretch"
                        spacing={2}
                        mb={3}
                      >
                        <Text fontSize="sm"><b>Naam:</b> {app.userName}</Text>
                        <Text fontSize="sm"><b>Email:</b> {app.email}</Text>
                        <Text fontSize="sm"><b>Tel:</b> {app.phoneNumber}</Text>
                        <Text fontSize="sm"><b>Tijd:</b> {formatAppointmentTime(app.appointment_datetime)}</Text>
                        <Text fontSize="sm"><b>Behandeling:</b> {app.treatmentName}</Text>

                        {app.treatmentName === 'Verwijdering tatoeage' ? (
                          <Text fontSize="sm" mt={2}>
                            <b>Lengte:</b> {app.length_cm} cm
                          </Text>
                        ) : (
                          app.treatmentOptions &&
                          app.treatmentOptions.length > 0 && (
                            <Box mt={2}>
                              <Text fontSize="sm" fontWeight="bold">Opties:</Text>
                              <List spacing={1}>
                                {app.treatmentOptions.map(option => (
                                  <ListItem key={option.treatmentOptionId}>
                                    {option.name} ({option.quantity}x)
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          )
                        )}
                        <Button size="sm" colorScheme="blue" width="full" ml={0} onClick={() => {
                          setSelectedAppointment(app);
                          setNewDate("");
                          setSelectedTime("");
                          setTimeSlots([]);
                          onOpen();
                        }}>
                          Verplaatsen
                        </Button>
                      </VStack>
                    )
                  )
                ) : (
                  <Text fontSize="xs" textAlign="center" color="gray.600">Geen afspraken</Text>
                )}
              </GridItem>
            ))}
          </Grid>

          <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Afspraak verplaatsen</ModalHeader>
              <ModalBody>
                <Text mb={2}>Nieuwe datum:</Text>
                <DatePicker
                  selected={newDate ? new Date(newDate) : null}
                  onChange={(date) => {
                    if (!date) return;
                    const formatted = formatDateISO(date);
                    setNewDate(formatted);
                    loadAvailableTimeSlots(formatted, setTimeSlots);
                  }}
                  dateFormat="yyyy-MM-dd"
                  filterDate={(day) => day.getDay() !== 5}
                  excludeDates={[...closures, ...fullyBookedDates]}
                  customInput={<CustomDateInput />}
                />

                <Text mt={2}>Nieuw tijdstip:</Text>
                {loadingSlots ? (
                  <Spinner />
                ) : (
                  <Select value={selectedTime} onChange={(e) => setSelectedTime(e.target.value)} placeholder="Kies een tijdslot">
                    {timeSlots.map((slot, index) => (
                      <option key={index} value={slot}>{slot}</option>
                    ))}
                  </Select>
                )}
              </ModalBody>
              {message && (
                <Text mt={2} color="red.500" fontSize="sm" textAlign={"center"}>
                  {message}
                </Text>
              )}
              <ModalFooter justifyContent="center">
                {isSaving ? (
                  <Spinner size="md" />
                ) : (
                  <>
                    <Button onClick={moveAppointment} colorScheme="blue" minW="120px">
                      Opslaan
                    </Button>
                    <Button onClick={onClose} ml={3} minW="120px">
                      Annuleren
                    </Button>
                  </>
                )}
              </ModalFooter>
            </ModalContent>
          </Modal>

          <Modal isOpen={isBlockingOpen} onClose={onCloseBlocking}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Leeg tijdslot blokkeren</ModalHeader>
              <ModalBody>
                <Text mb={2}>Datum:</Text>
                <DatePicker
                  selected={newBlockDate ? new Date(newBlockDate) : null}
                  onChange={(date) => {
                    if (!date) return;
                    const formatted = formatDateISO(date);
                    setNewBlockDate(formatted);
                    loadAvailableTimeSlots(formatted, setBlockTimeSlots);
                  }}
                  dateFormat="yyyy-MM-dd"
                  excludeDates={[...closures, ...fullyBookedDates]}
                  customInput={<CustomDateInput />}
                />

                <Text mt={2}>Tijdslot:</Text>
                {loadingSlots ? (
                  <Spinner />
                ) : (
                  <Box display="flex" flexWrap="wrap" gap={2} mt={2}>
                    {blockTimeSlots.map((slot) => {
                      const isSelected = selectedSlots.includes(slot);
                      return (
                        <Box
                          key={slot}
                          as="button"
                          border="2px solid"
                          borderColor={isSelected ? "red.500" : "gray.300"}
                          borderRadius="md"
                          bg={isSelected ? "red.100" : "white"}
                          color="black"
                          px={4}
                          py={2}
                          onClick={() => toggleSlot(slot)}
                        >
                          {slot}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </ModalBody>
              {blockMessage && (
                <Text mt={2} color="red.500" fontSize="sm" textAlign="center">
                  {blockMessage}
                </Text>
              )}
              <ModalFooter justifyContent="center">
                {isBlockingSaving ? (
                  <Spinner size="md" />
                ) : (
                  <>
                    <Button onClick={blockTimeSlot} colorScheme="red" minW="120px">
                      Blokkeren
                    </Button>
                    <Button onClick={onCloseBlocking} ml={3} minW="120px">
                      Annuleren
                    </Button>
                  </>
                )}
              </ModalFooter>
            </ModalContent>
          </Modal>
        </>
      )}
    </Box>
  );
};

export default ManageAppointments;